import React, { useState, useMemo, useCallback } from 'react';
import { useFormContext, useController } from 'react-hook-form';
import { Paper, Typography, InputBase, ClickAwayListener } from '@material-ui/core';
import { Search, ExpandMore, Close } from '@material-ui/icons';
import {
  Checkmark,
  IS_BLANK,
  IS_NOT_BLANK,
  UNUSED_FIELD_NAME,
} from '../../../constants/selectFilter.js';
import { MF_BLANK, MF_NOT_BLANK } from '../../../constants/selectFilter.js';
import RMSSelectFilterFooter from './RMSSelectFilterFooter.jsx';

const RMSSelectFilter = ({
  name,
  label,
  options = [],
  selected: selectedProp = [],
  onChange: onChangeProp,
  labelMap,
  disabled = false,
  sortOptions = true,
  valueKey = 'value',
  labelKey = 'label',
  // Lets a consumer control how an option is arranged in the open list (e.g. bolding a code),
  // without this component needing to know that shape. Receives the full option object. Defaults
  // to the plain label — most filters don't need anything fancier.
  renderOption,
  // Lets a consumer control what the trigger button shows when exactly one option is selected,
  // independently of renderOption. Receives the full option object. Defaults to the plain label.
  getSelectedLabel: getSelectedLabelProp,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const formContext = useFormContext();
  const control = formContext?.control;
  const isFormControlled = Boolean(name && control);

  const { field } = useController({
    name: isFormControlled ? name : UNUSED_FIELD_NAME,
    control,
    defaultValue: selectedProp,
    disabled: !isFormControlled,
  });

  const selected = isFormControlled ? field.value || [] : selectedProp;
  const onChange = isFormControlled ? field.onChange : onChangeProp;

  const optionValues = useMemo(
    () => options.map((o) => (typeof o === 'object' && o !== null ? o[valueKey] : o)),
    [options, valueKey]
  );

  const labelLookup = useMemo(() => {
    const map = {};
    options.forEach((o) => {
      if (typeof o === 'object' && o !== null) map[o[valueKey]] = o[labelKey] || o[valueKey];
    });
    if (labelMap) Object.assign(map, labelMap);
    return map;
  }, [options, labelMap, valueKey, labelKey]);

  const optionByValue = useMemo(() => {
    const map = {};
    options.forEach((o) => {
      if (typeof o === 'object' && o !== null) map[o[valueKey]] = o;
    });
    return map;
  }, [options, valueKey]);

  const isSentinel = (v) => v === MF_BLANK || v === MF_NOT_BLANK;

  const getLabel = useCallback(
    (val) => {
      if (val === MF_BLANK) return IS_BLANK;
      if (val === MF_NOT_BLANK) return IS_NOT_BLANK;
      const label = labelLookup[val] ?? val;
      return label == null ? '' : String(label);
    },
    [labelLookup]
  );

  const getOptionContent = useCallback(
    (val) => (renderOption ? renderOption(optionByValue[val]) : getLabel(val)),
    [renderOption, optionByValue, getLabel]
  );

  const getSelectedLabel = useCallback(
    (val) => {
      if (isSentinel(val)) return getLabel(val);
      return getSelectedLabelProp ? getSelectedLabelProp(optionByValue[val]) : getLabel(val);
    },
    [getSelectedLabelProp, optionByValue, getLabel]
  );

  const sorted = useMemo(() => {
    const nonSentinel = [...optionValues].filter((o) => !isSentinel(o));
    return sortOptions
      ? nonSentinel.sort((a, b) => getLabel(a).localeCompare(getLabel(b)))
      : nonSentinel;
  }, [optionValues, sortOptions, getLabel]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? sorted.filter((o) =>
          String(getLabel(o) ?? '')
            .toLowerCase()
            .includes(q)
        )
      : sorted;

    if (!q && selected.length > 0) {
      const sel = list.filter((o) => selected.includes(o));
      const unsel = list.filter((o) => !selected.includes(o));
      return [...sel, ...unsel];
    }
    return list;
  }, [sorted, query, selected, getLabel]);

  const toggle = (val) => {
    if (val === MF_BLANK) {
      const without = selected.filter((v) => v !== MF_NOT_BLANK && v !== MF_BLANK);
      onChange(selected.includes(MF_BLANK) ? without : [...without, MF_BLANK]);
    } else if (val === MF_NOT_BLANK) {
      const without = selected.filter((v) => v !== MF_BLANK && v !== MF_NOT_BLANK);
      onChange(selected.includes(MF_NOT_BLANK) ? without : [...without, MF_NOT_BLANK]);
    } else {
      onChange(selected.includes(val) ? selected.filter((v) => v !== val) : [...selected, val]);
    }
  };

  const isActive = selected.length > 0;
  const btnLabel =
    selected.length === 0
      ? `All ${label}`
      : selected.length === 1
      ? getSelectedLabel(selected[0])
      : `${selected.length} selected`;

  const nonSentinelTotal = optionValues.filter((o) => !isSentinel(o)).length;
  const nonSentinelSelected = selected.filter((v) => !isSentinel(v)).length;

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div className="multi-filter-wrapper">
        <div
          role="button"
          tabIndex={disabled ? -1 : 0}
          className={[
            'multi-filter-trigger-btn',
            isActive ? 'active' : '',
            disabled ? 'disabled' : '',
          ].join(' ')}
          onClick={() => {
            setOpen((o) => !o);
            setQuery('');
          }}
          onKeyDown={(e) => e.key === 'Enter' && setOpen((o) => !o)}
          aria-expanded={open}
          aria-haspopup="listbox">
          <Typography
            component="span"
            className={['multi-filter-trigger-label', isActive ? 'active' : ''].join(' ')}>
            {btnLabel}
          </Typography>

          {isActive && <span className="multi-filter-badge">{selected.length}</span>}

          <ExpandMore className={['multi-filter-chevron', open ? 'open' : ''].join(' ')} />
        </div>

        {open && (
          <Paper className="multi-filter-dropdown" elevation={0}>
            {/* Search */}
            <div className="multi-filter-search-wrap">
              <Search className="multi-filter-search-icon" />
              <InputBase
                className="multi-filter-search-input"
                placeholder={`Search ${label.toLowerCase()}…`}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoFocus
                inputProps={{ 'aria-label': `Search ${label}` }}
              />
              {query && (
                <Close className="multi-filter-clear-search" onClick={() => setQuery('')} />
              )}
            </div>

            <div className="multi-filter-options-list" role="listbox" aria-multiselectable="true">
              {visible.length === 0 ? (
                <div className="multi-filter-no-results">No matches for "{query}"</div>
              ) : (
                visible.map((opt) => {
                  const checked = selected.includes(opt);
                  return (
                    <div
                      key={opt}
                      role="option"
                      aria-selected={checked}
                      className={['multi-filter-option', checked ? 'checked' : ''].join(' ')}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        toggle(opt);
                      }}>
                      <div
                        className={['multi-filter-checkbox', checked ? 'checked' : ''].join(' ')}>
                        {checked && <Checkmark />}
                      </div>
                      <span
                        className={['multi-filter-option-label', checked ? 'checked' : ''].join(
                          ' '
                        )}>
                        {getOptionContent(opt)}
                      </span>
                    </div>
                  );
                })
              )}
            </div>

            <RMSSelectFilterFooter
              selected={selected}
              toggle={toggle}
              getLabel={getLabel}
              query={query}
              visible={visible}
              nonSentinelTotal={nonSentinelTotal}
              nonSentinelSelected={nonSentinelSelected}
              isActive={isActive}
              onChange={onChange}
            />
          </Paper>
        )}
      </div>
    </ClickAwayListener>
  );
};

export default RMSSelectFilter;
